import type {
  AiOperationsCurrentProposal,
  AiOperationsRunAction,
  ApprovalProposalDetail,
} from "@adpilot/shared";
import {
  IconBulb,
  IconCheck,
  IconCircleDot,
  IconHistory,
  IconUserCheck,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ApprovalStatusBadge } from "@/components/approval/approval-status-badge";
import { DataTable } from "@/components/patterns/DataTable";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatSignedCurrencyDiff, useFormatters } from "@/lib/format";

interface AiProposalRecordProps {
  proposal: AiOperationsCurrentProposal;
  actions: AiOperationsRunAction[];
  review?: ApprovalProposalDetail;
}

export function AiProposalRecord({ proposal, actions, review }: AiProposalRecordProps) {
  const { t } = useTranslation("aiOperations");
  const { formatCurrency, formatPercent, formatDateTime } = useFormatters();
  const { artifact } = proposal;
  const actionState = new Map(actions.map((action) => [action.id, action]));
  const reviewedAction = new Map(review?.actions.map((action) => [action.id, action]) ?? []);
  const appliedDecision = new Map(
    review?.decisionHistory
      .filter(({ phase, decision }) => phase === "applied" && decision !== "finalize")
      .map((event) => [event.actionId, event]) ?? [],
  );

  return (
    <div className="flex flex-col gap-5 px-4 pb-6">
      <Card className="gap-3 py-4">
        <CardHeader className="border-b">
          <div className="flex min-w-0 items-center gap-2">
            <IconBulb className="size-4 shrink-0 text-brand" aria-hidden />
            <CardTitle>{t("runDetail.record.analysisTitle")}</CardTitle>
            <Badge variant="outline" className="ml-auto shrink-0">
              {t(`runDetail.record.health.${artifact.analysis.campaignHealth}`)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="max-w-[72ch] text-sm font-medium leading-6 text-ink">
            {artifact.analysis.summary}
          </p>
          {artifact.analysis.keyFindings.length > 0 ? (
            <ul className="flex flex-col gap-2 text-xs leading-5 text-muted-foreground">
              {artifact.analysis.keyFindings.map((finding) => (
                <li key={finding} className="flex items-start gap-2">
                  <IconCircleDot className="mt-1 size-3 shrink-0" aria-hidden />
                  <span>{finding}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </CardContent>
      </Card>

      <section className="flex flex-col gap-3" aria-labelledby="proposal-actions-heading">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h3 id="proposal-actions-heading" className="text-sm font-semibold text-ink">
              {t("runDetail.record.actionsTitle")}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("runDetail.record.actionsDescription")}
            </p>
          </div>
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {t("runDetail.record.actionTotal", { count: artifact.actions.length })}
          </span>
        </div>

        {artifact.actions.length === 0 ? (
          <Alert>
            <IconCheck />
            <AlertTitle>{t("runDetail.record.noActionsTitle")}</AlertTitle>
            <AlertDescription>{t("runDetail.record.noActionsBody")}</AlertDescription>
          </Alert>
        ) : (
          <div className="overflow-hidden rounded-lg border border-line bg-surface">
            {artifact.actions.map((action, index) => {
              const state = actionState.get(action.id);
              const human = reviewedAction.get(action.id);
              const decision = appliedDecision.get(action.id);
              return (
                <article key={action.id} className="flex flex-col">
                  {index > 0 ? <Separator /> : null}
                  <div className="grid gap-4 px-4 py-4 lg:grid-cols-[minmax(0,1fr)_220px]">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="truncate text-sm font-semibold text-ink">
                          {action.target.label}
                        </h4>
                        <Badge variant="outline">
                          {t(`runDetail.record.actionType.${action.agent.actionType}`)}
                        </Badge>
                        <ApprovalStatusBadge value={action.server.riskLevel} />
                      </div>
                      <p className="mt-2 max-w-[72ch] text-sm leading-6 text-ink">
                        {action.agent.reasoning}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-mono tabular-nums text-ink">
                          {formatCurrency(action.server.oldBid)} →{" "}
                          {formatCurrency(action.server.newBid)}
                        </span>
                        <span>·</span>
                        <span className="font-mono tabular-nums">
                          {formatSignedCurrencyDiff(
                            action.server.oldBid,
                            action.server.newBid,
                            formatCurrency,
                          )}
                        </span>
                        <span>·</span>
                        <span>
                          {t("runDetail.record.confidence", {
                            value: t(`runDetail.record.confidenceValue.${action.agent.confidence}`),
                          })}
                        </span>
                      </div>
                      {action.server.trustedMetrics ? (
                        <p className="mt-2 font-mono text-[11px] leading-5 tabular-nums text-muted-foreground">
                          {t("runDetail.record.evidenceLine", {
                            windowStart: action.server.trustedMetrics.windowStart,
                            windowEnd: action.server.trustedMetrics.windowEnd,
                            clicks: action.server.trustedMetrics.clicks,
                            spend: formatCurrency(action.server.trustedMetrics.spend),
                            sales: formatCurrency(action.server.trustedMetrics.sales),
                            acos: formatPercent(action.server.trustedMetrics.acos),
                          })}
                        </p>
                      ) : null}
                    </div>

                    <div className="flex flex-col gap-3 rounded-md bg-subtle/55 p-3">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant="outline">
                          {t("runDetail.record.route", {
                            value: t(`runDetail.record.routeValue.${action.server.route}`),
                          })}
                        </Badge>
                        {state ? (
                          <Badge variant="outline">
                            {t("runDetail.record.executionState", {
                              value: t(`runDetail.record.actionState.${state.status}`),
                            })}
                          </Badge>
                        ) : null}
                      </div>
                      <Separator />
                      <div className="flex items-start gap-2">
                        <IconUserCheck
                          className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                          aria-hidden
                        />
                        <div className="min-w-0 text-xs">
                          <p className="font-medium text-ink">
                            {human
                              ? t(`runDetail.record.human.${human.reviewStatus}`)
                              : t("runDetail.record.human.notReviewed")}
                          </p>
                          {decision ? (
                            <p className="mt-1 text-muted-foreground">
                              {decision.note ??
                                (decision.reasonCode
                                  ? decision.reasonCode.replaceAll("_", " ")
                                  : t(`runDetail.record.decision.${decision.decision}`))}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {artifact.conclusions.length > 0 ? (
        <section className="flex flex-col gap-3" aria-labelledby="proposal-conclusions-heading">
          <div>
            <h3 id="proposal-conclusions-heading" className="text-sm font-semibold text-ink">
              {t("runDetail.record.conclusionsTitle")}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("runDetail.record.conclusionsDescription")}
            </p>
          </div>
          <DataTable density="compact">
            <TableHeader>
              <TableRow>
                <TableHead>{t("runDetail.record.columns.target")}</TableHead>
                <TableHead>{t("runDetail.record.columns.conclusion")}</TableHead>
                <TableHead>{t("runDetail.record.columns.reason")}</TableHead>
                <TableHead>{t("runDetail.record.columns.confidence")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {artifact.conclusions.map((conclusion) => (
                <TableRow key={conclusion.id}>
                  <TableCell className="font-medium">{conclusion.target.label}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {t(`runDetail.record.conclusion.${conclusion.agent.conclusion}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[520px] whitespace-normal leading-5">
                    {conclusion.agent.reasoning}
                  </TableCell>
                  <TableCell>
                    {t(`runDetail.record.confidenceValue.${conclusion.agent.confidence}`)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        </section>
      ) : null}

      <section className="grid gap-4 lg:grid-cols-2">
        <Card className="gap-3 py-4">
          <CardHeader className="border-b">
            <CardTitle>{t("runDetail.record.policyTitle")}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-5 gap-y-3 text-xs">
              <Fact
                label={t("runDetail.record.policy.goal")}
                value={t(`policy.goal.${artifact.validation.effectivePolicy.businessGoal}`)}
              />
              <Fact
                label={t("runDetail.record.policy.targetAcos")}
                value={formatPercent(artifact.validation.effectivePolicy.targetAcos)}
              />
              <Fact
                label={t("runDetail.record.policy.approval")}
                value={t(
                  `runDetail.record.policy.approvalValue.${artifact.validation.effectivePolicy.approvalRiskThreshold}`,
                )}
              />
              <Fact
                label={t("runDetail.record.policy.risk")}
                value={t(
                  `runDetail.record.policy.riskValue.${artifact.validation.effectivePolicy.riskPreference}`,
                )}
              />
              <Fact
                label={t("runDetail.record.policy.mode")}
                value={
                  artifact.validation.effectivePolicy.dryRun
                    ? t("runDetail.record.policy.dryRun")
                    : t("runDetail.record.policy.live")
                }
              />
              <Fact
                label={t("runDetail.record.policy.snapshot")}
                value={artifact.validation.policySnapshotId.slice(0, 8).toUpperCase()}
                mono
              />
            </dl>
          </CardContent>
        </Card>

        <Card className="gap-3 py-4">
          <CardHeader className="border-b">
            <div className="flex items-center gap-2">
              <IconHistory className="size-4 text-muted-foreground" aria-hidden />
              <CardTitle>{t("runDetail.record.auditTitle")}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <dl className="flex flex-col gap-2 text-xs">
              <Fact
                label={t("runDetail.record.audit.round")}
                value={String(proposal.roundNumber)}
                mono
              />
              <Fact
                label={t("runDetail.record.audit.attempt")}
                value={`${artifact.validation.candidateAttempt}/3`}
                mono
              />
              <Fact
                label={t("runDetail.record.audit.validated")}
                value={formatDateTime(artifact.validation.validatedAt)}
              />
              <Fact
                label={t("runDetail.record.audit.proposal")}
                value={proposal.id.slice(0, 8).toUpperCase()}
                mono
              />
              <Fact
                label={t("runDetail.record.audit.state")}
                value={
                  proposal.closedReason
                    ? t("runDetail.record.audit.closedForRevision")
                    : t("runDetail.record.audit.current")
                }
              />
            </dl>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function Fact({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex min-w-0 items-start justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={
          mono ? "truncate font-mono tabular-nums text-ink" : "truncate capitalize text-ink"
        }
      >
        {value}
      </dd>
    </div>
  );
}
